import { injectable } from 'inversify';
import * as TrackingApi from '../../tracking-api';
import { SuspendDataStoreError } from './suspend-data-store';
import { reportNonFatalError } from '@/app/error-handling';

@injectable()
export class SuspendDataStateStore<TData extends { id: string }, TError, TCommand> {

  private localState: TData[] = [];
  private remoteFetched: boolean = false;
  private commandQueue: TCommand[] = [];

  constructor(
    private loadState: () => Promise<TrackingApi.Result<TData[] | null, SuspendDataStoreError>>,
    private updateState: (items: TData[]) => Promise<TrackingApi.Result<true, SuspendDataStoreError>>,
    private defaultState: (id: string) => TData,
    private reducer: (state: TData[], command: TCommand) => TData[]) {
  }

  public async execute(command: TCommand) {
    return await this.applyCommand(command);
  }

  public async query(ids: string[] | 'all'): Promise<TData[]> {

    await this.prefetch();

    if (ids === 'all') {
      return this.localState;
    }

    return ids.map((id) => {
      return this.localState.find((x) => x.id === id) || this.defaultState(id);
    });
  }

  private async applyCommand(command: TCommand) {

    const fetchResult = await this.prefetch();
    if (fetchResult === false) {
      // queue the command
      this.commandQueue.push(command);
    } else if (fetchResult === 'fetched') {
      // apply queued commands
      for (const cmd of this.commandQueue) {
        this.localState = this.reducer(this.localState, cmd);
      }
      this.commandQueue = [];
    }

    // apply the current command
    this.localState = this.reducer(this.localState, command);

    if (this.remoteFetched) {
      // update the remote state
      // TODO: Error handling, retry handling
      const result = await this.updateState(this.localState);
      if (!result.success) {
        reportNonFatalError('Coding error: Error while updating: ' + JSON.stringify(result.error))
      }
    }
  }

  private async prefetch(): Promise<false | 'fetched' | 'already-fetched'> {
    if (this.remoteFetched) {
      return 'already-fetched';
    }

    const result = await this.loadState();
    if (result.success) {
      this.remoteFetched = true;
      this.localState = result.value || [];
      return 'fetched';
    } else {
      return false;
    }
  }
}
